import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ProjectRoadmap from '../components/ProjectRoadmap';

const Projecten = () => {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const [projecten, setProjecten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('actief');
  const [afdScope, setAfdScope] = useState('eigen'); // 'eigen' | 'alle'
  const [activeTab, setActiveTab] = useState('lijst'); // 'lijst' | 'roadmap'
  const [formData, setFormData] = useState({
    project_code: '', naam: '', beschrijving: '', start_datum: '', eind_datum: '', ordernummer: ''
  });
  const [ordernummerLijst, setOrdernummerLijst] = useState([]);
  useEffect(() => { api.get('/ordernummers').then(r => setOrdernummerLijst(r.data.ordernummers || [])).catch(() => {}); }, []);

  useEffect(() => {
    loadProjecten();
  }, [afdScope]); // eslint-disable-line

  const loadProjecten = async () => {
    try {
      const res = await api.get('/projecten' + (afdScope === 'alle' ? '?scope=alle' : ''));
      setProjecten(res.data.projecten || []);
    } catch (error) {
      console.error('Fout bij laden projecten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projecten', formData);
      setShowModal(false);
      setFormData({ project_code: '', naam: '', beschrijving: '', start_datum: '', eind_datum: '', ordernummer: '' });
      loadProjecten();
    } catch (error) {
      alert(error.response?.data?.message || 'Fout bij aanmaken project');
    }
  };

  const generateCode = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData({ ...formData, project_code: `PRJ-${year}-${random}` });
  };

  const getStatusBadge = (status) => {
    const styles = {
      nieuw: { bg: '#e0f2fe', color: '#0369a1', label: 'Nieuw' },
      actief: { bg: '#dcfce7', color: '#166534', label: 'Actief' },
      afgerond: { bg: '#f3f4f6', color: '#6b7280', label: 'Afgerond' }
    };
    const s = styles[status] || styles.nieuw;
    return <span style={{ background: s.bg, color: s.color, padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{s.label}</span>;
  };

  const getWaarschuwingBadge = (w) => {
    if (!w) return null;
    if (w === 'over_deadline') return <span style={{ background: '#fee2e2', color: '#dc2626', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', marginLeft: '8px' }}>🔴 Over deadline!</span>;
    if (w === 'bijna_deadline') return <span style={{ background: '#fef3c7', color: '#d97706', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', marginLeft: '8px' }}>⚠️ Bijna deadline</span>;
    return null;
  };

  const filteredProjecten = projecten.filter(p => {
    if (filter === 'alle') return true;
    if (filter === 'actief') return p.status === 'actief' || p.status === 'nieuw';
    if (filter === 'afgerond') return p.status === 'afgerond';
    if (filter === 'waarschuwing') return p.waarschuwing;
    return true;
  });

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>⏳ Laden...</div>;

  return (
    <div className="projecten-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>📁 Projecten</h1>
        </div>
        <div style={{ padding: '15px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '20px' }}>
          <button onClick={() => setShowModal(true)} className="btn-primary">+ Nieuw Project</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('lijst')}
            style={{
              padding: '10px 20px', border: 'none', background: 'transparent',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              borderBottom: activeTab === 'lijst' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'lijst' ? '#3b82f6' : '#6b7280',
              marginBottom: -2
            }}
          >📋 Lijst</button>
          <button
            onClick={() => setActiveTab('roadmap')}
            style={{
              padding: '10px 20px', border: 'none', background: 'transparent',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              borderBottom: activeTab === 'roadmap' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'roadmap' ? '#3b82f6' : '#6b7280',
              marginBottom: -2
            }}
          >🗓️ Roadmap</button>
        </div>

        {activeTab === 'roadmap' && (
          <ProjectRoadmap
            projecten={projecten}
            onProjectClick={(id) => navigate('/projecten/' + id)}
          />
        )}

        {activeTab === 'lijst' && (<>
        {/* Afdeling-scope (verborgen voor monteurs) */}
        {!(gebruiker && (gebruiker.rol === 'monteur' || gebruiker.rol_id === 3)) && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {[['eigen', 'Mijn afdeling'], ['alle', 'Alle afdelingen']].map(([s, label]) => (
              <button key={s} onClick={() => setAfdScope(s)} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid ' + (afdScope === s ? '#00D4AA' : '#d1d5db'), background: afdScope === s ? '#00D4AA' : '#fff', color: afdScope === s ? '#0a0e1a' : '#374151', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          {['alle', 'actief', 'afgerond', 'waarschuwing'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: filter === f ? '#3b82f6' : '#e5e7eb', color: filter === f ? 'white' : '#374151', cursor: 'pointer', fontWeight: '500' }}>
              {f === 'alle' && `Alle (${projecten.length})`}
              {f === 'actief' && `Actief (${projecten.filter(p => p.status === 'actief' || p.status === 'nieuw').length})`}
              {f === 'afgerond' && `Afgerond (${projecten.filter(p => p.status === 'afgerond').length})`}
              {f === 'waarschuwing' && `⚠️ Waarschuwing (${projecten.filter(p => p.waarschuwing).length})`}
            </button>
          ))}
        </div>

        {/* Projecten Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {filteredProjecten.map(project => (
            <div key={project.id} onClick={() => navigate(`/projecten/${project.id}`)} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', borderLeft: project.waarschuwing === 'over_deadline' ? '4px solid #dc2626' : project.waarschuwing === 'bijna_deadline' ? '4px solid #f59e0b' : '4px solid #3b82f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <span style={{ color: '#6b7280', fontSize: '12px', fontFamily: 'monospace' }}>{project.project_code}</span>
                  {project.ordernummer && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#e0f2fe', color: '#0369a1', fontFamily: 'monospace' }}>📋 {project.ordernummer}</span>}
                  <h3 style={{ margin: '4px 0 0 0', fontSize: '18px' }}>{project.naam}</h3>
                </div>
                {getStatusBadge(project.status)}
              </div>
              {project.beschrijving && <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 15px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.beschrijving}</p>}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}><span>Voortgang</span><span>{project.totaal_voortgang}%</span></div>
                <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '8px', overflow: 'hidden' }}><div style={{ width: `${project.totaal_voortgang}%`, height: '100%', background: project.totaal_voortgang === 100 ? '#10b981' : '#3b82f6' }} /></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280' }}>
                <span>📋 {project.afgeronde_taken}/{project.totaal_taken} taken</span>
                <span>🔧 {project.afgeronde_werkorders}/{project.totaal_werkorders} werkorders</span>
              </div>
              {project.eind_datum && <div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>📅 Deadline: {new Date(project.eind_datum).toLocaleDateString('nl-NL')}{getWaarschuwingBadge(project.waarschuwing)}</div>}
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>{project.afdeling_naam} • {project.aangemaakt_door_naam}</div>
            </div>
          ))}
        </div>

        {filteredProjecten.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
            <p style={{ fontSize: '48px', margin: '0 0 20px 0' }}>📁</p>
            <p>Geen projecten gevonden</p>
            <button onClick={() => setShowModal(true)} className="btn-primary" style={{ marginTop: '20px' }}>Maak eerste project</button>
          </div>
        )}
        </>)}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%' }}>
            <h2 style={{ marginTop: 0 }}>📁 Nieuw Project</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Project Code *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" value={formData.project_code} onChange={e => setFormData({ ...formData, project_code: e.target.value })} placeholder="PRJ-2026-001" required style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                  <button type="button" onClick={generateCode} style={{ padding: '10px 15px', background: '#e5e7eb', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🎲</button>
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Ordernummer</label>
                <select value={formData.ordernummer} onChange={e => setFormData({ ...formData, ordernummer: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box', background: 'white' }}>
                  <option value="">- geen -</option>
                  {ordernummerLijst.map(o => <option key={o.id} value={o.nummer}>{o.nummer}{o.omschrijving ? ' - ' + o.omschrijving : ''}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Naam *</label>
                <input type="text" value={formData.naam} onChange={e => setFormData({ ...formData, naam: e.target.value })} placeholder="Project naam" required style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Beschrijving</label>
                <textarea value={formData.beschrijving} onChange={e => setFormData({ ...formData, beschrijving: e.target.value })} rows={3} placeholder="Korte beschrijving..." style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Startdatum</label>
                  <input type="date" value={formData.start_datum} onChange={e => setFormData({ ...formData, start_datum: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Deadline</label>
                  <input type="date" value={formData.eind_datum} onChange={e => setFormData({ ...formData, eind_datum: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Aanmaken</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projecten;
